/**
 * Simulation Common Hooks - All-in-One
 *
 * Comprehensive collection of reusable hooks for all simulation components.
 * Combines undo, grid operations, VBC support, and utility functions.
 *
 * @module hooks/simulation
 *
 * Available Hooks:
 * 1. useSimulationUndo - Row-level undo functionality
 * 2. useSimulationGrid - AG Grid operations and pagination
 * 3. useSimulationVBC - VBC/ZBC/CBC costing type support
 * 4. useSimulationConfig - Configuration and decimal places
 * 5. useSimulationCalculations - Common calculation helpers
 *
 * Usage:
 * import {
 *   useSimulationUndo,
 *   useSimulationGrid,
 *   useSimulationVBC
 * } from '../../hooks/simulation';
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import Toaster from '../../components/common/Toaster';

// ============================================================================
// 1. UNDO/REDO HOOK - Row-level undo/redo with LIFO behavior
// ============================================================================

export const useSimulationUndo = ({
    gridApi,
    findNodeByRowId,
    disabled = false,
    maxStackSize = 50,
    getRowId = (row) => row.BoughtOutPartId || row.CostingId,
    enableKeyboardShortcuts = true,
    onUndoSuccess,
    onUndoError,
    onRedoSuccess,
    onRedoError,
    updateListData, // Callback to update the list array
    initialData // NEW: Initial data for reset functionality
}) => {
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [initialSnapshot, setInitialSnapshot] = useState(null);

    // Save initial state on first render for reset functionality
    useEffect(() => {
        if (initialData && initialData.length > 0 && !initialSnapshot) {

            setInitialSnapshot(JSON.parse(JSON.stringify(initialData)));
        }
    }, [initialData, initialSnapshot]);

    const saveUndoState = useCallback((oldRowData, description = 'Row changes') => {
        if (disabled) {
            return;
        }
        if (!oldRowData) {
            return;
        }

        const rowId = getRowId(oldRowData);

        // Clear redo stack when a new action is performed
        setRedoStack([]);

        setUndoStack(prev => {
            const existingIndex = prev.findIndex(item => item.rowId === rowId);

            if (existingIndex !== -1) {
                // Row already exists - remove it from current position and add to end (LIFO by most recent edit)
                const existingSnapshot = prev[existingIndex];

                // Remove from current position
                const updatedStack = prev.filter((_, index) => index !== existingIndex);

                // Add to end with updated timestamp
                const newStack = [...updatedStack, {
                    ...existingSnapshot,
                    timestamp: Date.now(),
                    description
                }];

                return newStack;
            } else {
                const snapshot = {
                    timestamp: Date.now(),
                    description,
                    rowId,
                    oldData: JSON.parse(JSON.stringify(oldRowData))
                };

                const newStack = [...prev, snapshot];

                if (newStack.length > maxStackSize) {
                    return newStack.slice(newStack.length - maxStackSize);
                }
                return newStack;
            }
        });
    }, [disabled, getRowId, maxStackSize]);

    const handleUndo = useCallback(() => {

        if (undoStack.length === 0) {
            Toaster.info('Nothing to undo. Make a change first. (Tip: Edits are tracked per row)');
            return;
        }

        if (disabled) {
            Toaster.info('Undo is disabled for this view.');
            return;
        }

        if (!gridApi) {
            console.error('Grid API not available');
            Toaster.error('Unable to undo: Grid not ready');
            return;
        }

        const lastState = undoStack[undoStack.length - 1];
        const { rowId, oldData, description } = lastState;


        try {
            const node = findNodeByRowId(rowId);
            if (node) {
                // Build concise row identifier for toast (prefer BOP Name/Number)
                const d = node.data || {};
                const partName = d.BoughtOutPartName || d.PartName || d.BOPName || '';
                const partNo = d.BoughtOutPartNumber || d.PartNumber || d.BOPNumber || '';
                const masterName = d.CostingHead || d.CustomerName || d.Vendor || '';
                const rowIdentifier = partName && partNo
                    ? `${partName} (${partNo})`
                    : (partName || partNo || masterName || `${rowId}`);
                // Save current state to redo stack BEFORE undoing
                const currentData = JSON.parse(JSON.stringify(node.data));
                setRedoStack(prev => [...prev, {
                    timestamp: Date.now(),
                    description,
                    rowId,
                    newData: currentData
                }]);

                const restoredData = JSON.parse(JSON.stringify(oldData));

                setUndoStack(prev => prev.filter(item => item.rowId !== rowId));


                // STEP 1: Update the underlying list array FIRST
                if (updateListData) {
                    updateListData(rowId, restoredData);

                }

                // STEP 2: Update the grid node
                node.setData(restoredData);

                // STEP 3: Force refresh all cells for this row
                gridApi.refreshCells({
                    rowNodes: [node],
                    force: true
                });


                // Show concise identifier with explicit action label
                Toaster.success(`Undo: ${rowIdentifier}`);

                if (onUndoSuccess) {
                    onUndoSuccess(restoredData, rowId, node.rowIndex);
                }
            } else {
                console.error('Node not found for rowId:', rowId);
                Toaster.error('Unable to find row to undo');
            }
        } catch (error) {
            console.error('Error during undo:', error);
            Toaster.error('Unable to undo: An error occurred');

            if (onUndoError) {
                onUndoError(error);
            }
        }
    }, [undoStack, disabled, gridApi, findNodeByRowId, onUndoSuccess, onUndoError, updateListData]);

    const handleRedo = useCallback(() => {


        if (redoStack.length === 0) {
            Toaster.info('Nothing to redo. Use Undo first.');
            return;
        }

        if (disabled) {
            Toaster.info('Redo is disabled for this view.');
            return;
        }

        if (!gridApi) {
            console.error('Grid API not available');
            Toaster.error('Unable to redo: Grid not ready');
            return;
        }

        const lastRedoState = redoStack[redoStack.length - 1];
        const { rowId, newData, description } = lastRedoState;



        try {
            const node = findNodeByRowId(rowId);
            if (node) {
                // Build concise row identifier for toast (prefer BOP Name/Number)
                const d = node.data || {};
                const partName = d.BoughtOutPartName || d.PartName || d.BOPName || '';
                const partNo = d.BoughtOutPartNumber || d.PartNumber || d.BOPNumber || '';
                const masterName = d.CostingHead || d.CustomerName || d.Vendor || '';
                const rowIdentifier = partName && partNo
                    ? `${partName} (${partNo})`
                    : (partName || partNo || masterName || `${rowId}`);
                // Save current state to undo stack BEFORE redoing
                const currentData = JSON.parse(JSON.stringify(node.data));
                setUndoStack(prev => [...prev, {
                    timestamp: Date.now(),
                    description,
                    rowId,
                    oldData: currentData
                }]);

                const restoredData = JSON.parse(JSON.stringify(newData));


                setRedoStack(prev => prev.slice(0, -1));




                // STEP 1: Update the underlying list array FIRST
                if (updateListData) {
                    updateListData(rowId, restoredData);

                }

                // STEP 2: Update the grid node
                node.setData(restoredData);

                // STEP 3: Force refresh all cells for this row
                gridApi.refreshCells({
                    rowNodes: [node],
                    force: true
                });


                // Show concise identifier with explicit action label
                Toaster.success(`Redo: ${rowIdentifier}`);

                if (onRedoSuccess) {
                    onRedoSuccess(restoredData, rowId, node.rowIndex);
                }
            } else {
                console.error('Node not found for rowId:', rowId);
                Toaster.error('Unable to find row to redo');
            }
        } catch (error) {
            console.error('Error during redo:', error);
            Toaster.error('Unable to redo: An error occurred');

            if (onRedoError) {
                onRedoError(error);
            }
        }
    }, [redoStack, disabled, gridApi, findNodeByRowId, onRedoSuccess, onRedoError, updateListData]);

    const handleReset = useCallback(() => {


        if (!initialSnapshot || initialSnapshot.length === 0) {
            Toaster.info('No initial state available to reset');
            return;
        }

        if (disabled) {
            Toaster.info('Reset is disabled');
            return;
        }

        if (!gridApi) {
            console.error('Grid API not available');
            Toaster.error('Unable to reset: Grid not ready');
            return;
        }

        try {


            // Reset each row to its initial state
            const resetData = JSON.parse(JSON.stringify(initialSnapshot));

            resetData.forEach((rowData) => {
                const rowId = getRowId(rowData);
                const node = findNodeByRowId(rowId);

                if (node) {
                    // Update the underlying list array
                    if (updateListData) {
                        updateListData(rowId, rowData);
                    }

                    // Update the grid node
                    node.setData(rowData);
                }
            });

            // Force refresh all cells
            gridApi.refreshCells({ force: true });

            // Clear both undo and redo stacks
            setUndoStack([]);
            setRedoStack([]);


            const count = Array.isArray(resetData) ? resetData.length : 0;
            Toaster.success(`Reset: Restored ${count} row${count === 1 ? '' : 's'} to initial state`);
        } catch (error) {
            console.error('Error during reset:', error);
            Toaster.error('Unable to reset: An error occurred');
        }
    }, [initialSnapshot, disabled, gridApi, findNodeByRowId, getRowId, updateListData]);

    const clearUndoStack = useCallback(() => {
        setUndoStack([]);

    }, []);

    const clearRedoStack = useCallback(() => {
        setRedoStack([]);

    }, []);

    const clearAllStacks = useCallback(() => {
        setUndoStack([]);
        setRedoStack([]);

    }, []);

    const canUndo = undoStack.length > 0 && !disabled;
    const canRedo = redoStack.length > 0 && !disabled;

    useEffect(() => {
        if (!enableKeyboardShortcuts) return;

        const handleKeyDown = (e) => {
            // Ctrl+Z for undo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !disabled) {
                e.preventDefault();
                handleUndo();
            }
            // Ctrl+Y for redo
            if ((e.ctrlKey || e.metaKey) && e.key === 'y' && !disabled) {
                e.preventDefault();
                handleRedo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo, disabled, enableKeyboardShortcuts]);

    return {
        undoStack,
        redoStack,
        saveUndoState,
        handleUndo,
        handleRedo,
        handleReset,
        clearUndoStack,
        clearRedoStack,
        clearAllStacks,
        canUndo,
        canRedo,
        undoCount: undoStack.length,
        redoCount: redoStack.length
    };
};

// ============================================================================
// 2. GRID HOOK - AG Grid operations and pagination
// ============================================================================

export const useSimulationGrid = ({
    getRowId = (row) => row.BoughtOutPartId || row.CostingId,
    defaultPageSize = 30,
    onGridReadyCallback,
    onPageSizeChangedCallback
} = {}) => {
    const [gridApi, setGridApi] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(defaultPageSize);

    const findNodeByRowId = useCallback((rowId) => {
        if (!gridApi) {
            console.warn('Grid API not available');
            return null;
        }

        let foundNode = null;
        gridApi.forEachNode(node => {
            if (node.data) {
                const nodeId = getRowId(node.data);
                if (nodeId === rowId) {
                    foundNode = node;
                }
            }
        });

        return foundNode;
    }, [gridApi, getRowId]);

    const refreshGridRow = useCallback((rowId) => {
        if (!gridApi) {
            console.warn('Grid API not available for refresh');
            return false;
        }

        const node = findNodeByRowId(rowId);

        if (node) {
            gridApi.refreshCells({
                rowNodes: [node],
                force: true
            });

            return true;
        } else {
            console.warn('Row node not found for refresh:', rowId);
            return false;
        }
    }, [gridApi, findNodeByRowId]);

    const refreshMultipleRows = useCallback((rowIds) => {
        if (!gridApi) {
            console.warn('Grid API not available for batch refresh');
            return 0;
        }

        const nodes = rowIds
            .map(rowId => findNodeByRowId(rowId))
            .filter(node => node !== null);

        if (nodes.length > 0) {
            gridApi.refreshCells({
                rowNodes: nodes,
                force: true
            });

            return nodes.length;
        } else {
            console.warn('No valid nodes found for batch refresh');
            return 0;
        }
    }, [gridApi, findNodeByRowId]);

    const saveCurrentPagination = useCallback(() => {
        if (gridApi) {
            setCurrentPage(gridApi.paginationGetCurrentPage());
            setCurrentPageSize(gridApi.paginationGetPageSize());

        }
    }, [gridApi]);

    const restorePagination = useCallback(() => {
        if (gridApi) {
            gridApi.paginationGoToPage(currentPage);
        }
    }, [gridApi, currentPage]);

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);

        if (currentPage > 0) {
            setTimeout(() => {
                params.api.paginationGoToPage(currentPage);
            }, 0);
        }

        if (onGridReadyCallback) {
            onGridReadyCallback(params);
        }
    }, [currentPage, onGridReadyCallback]);

    const onPageSizeChanged = useCallback((newPageSize) => {

        setCurrentPageSize(Number(newPageSize));

        if (onPageSizeChangedCallback) {
            onPageSizeChangedCallback(newPageSize);
        }
    }, [onPageSizeChangedCallback]);

    const applyTransaction = useCallback((transaction) => {
        if (!gridApi) {
            console.error('Grid API not available for transaction');
            return null;
        }


        const result = gridApi.applyTransaction(transaction);


        return result;
    }, [gridApi]);

    return {
        gridApi,
        setGridApi,
        currentPage,
        currentPageSize,
        setCurrentPage,
        setCurrentPageSize,
        findNodeByRowId,
        refreshGridRow,
        refreshMultipleRows,
        saveCurrentPagination,
        restorePagination,
        onGridReady,
        onPageSizeChanged,
        applyTransaction
    };
};

// ============================================================================
// 3. VBC HOOK - Costing type detection and support
// ============================================================================

export const useSimulationVBC = ({
    list,
    ZBCTypeId,
    VBCTypeId,
    CBCTypeId
}) => {
    const hasZBCRows = useMemo(() => {
        if (!list || list.length === 0) return false;
        return list.some(row => row.CostingTypeId === ZBCTypeId);
    }, [list, ZBCTypeId]);

    const hasVBCRows = useMemo(() => {
        if (!list || list.length === 0) return false;
        return list.some(row => row.CostingTypeId === VBCTypeId || row.VBCTypeId === VBCTypeId);
    }, [list, VBCTypeId]);

    const hasCBCRows = useMemo(() => {
        if (!list || list.length === 0) return false;
        return list.some(row => row.CostingTypeId === CBCTypeId);
    }, [list, CBCTypeId]);

    const shouldShowConditionCostColumn = useMemo(() => {
        return hasZBCRows;
    }, [hasZBCRows]);

    const getRowCostingType = useCallback((row) => {
        if (!row) return 'UNKNOWN';
        if (row.CostingTypeId === ZBCTypeId) return 'ZBC';
        if (row.CostingTypeId === VBCTypeId || row.VBCTypeId === VBCTypeId) return 'VBC';
        if (row.CostingTypeId === CBCTypeId) return 'CBC';
        return 'UNKNOWN';
    }, [ZBCTypeId, VBCTypeId, CBCTypeId]);

    const isVBCRow = useCallback((row) => {
        if (!row) return false;
        return row.CostingTypeId === VBCTypeId || row.VBCTypeId === VBCTypeId;
    }, [VBCTypeId]);

    const isZBCRow = useCallback((row) => {
        if (!row) return false;
        return row.CostingTypeId === ZBCTypeId;
    }, [ZBCTypeId]);

    const isCBCRow = useCallback((row) => {
        if (!row) return false;
        return row.CostingTypeId === CBCTypeId;
    }, [CBCTypeId]);

    return {
        hasZBCRows,
        hasVBCRows,
        hasCBCRows,
        shouldShowConditionCostColumn,
        getRowCostingType,
        isVBCRow,
        isZBCRow,
        isCBCRow
    };
};

// ============================================================================
// 4. CONFIG HOOK - Configuration and decimal places
// ============================================================================

export const useSimulationConfig = (configurationKey) => {
    const decimalPlaces = useMemo(() => {
        return configurationKey?.NoOfDecimalPlaces || 2;
    }, [configurationKey]);

    const formatNumber = useCallback((value, decimals = decimalPlaces) => {
        if (value === null || value === undefined || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return '';
        return num.toFixed(decimals);
    }, [decimalPlaces]);

    const isVisible = useCallback((key) => {
        return configurationKey?.[key] === true;
    }, [configurationKey]);

    return {
        decimalPlaces,
        formatNumber,
        isVisible,
        config: configurationKey
    };
};

// ============================================================================
// 5. CALCULATIONS HOOK - Common calculation helpers
// ============================================================================

export const useSimulationCalculations = () => {
    const checkForNull = useCallback((value) => {
        const val = Number(value);
        return isNaN(val) ? 0 : val;
    }, []);

    const calculateNetLandedCost = useCallback((basicRate, otherCost, conditionCost, numberOfPieces) => {
        return ((checkForNull(basicRate) + checkForNull(otherCost)) / checkForNull(numberOfPieces)) + checkForNull(conditionCost);
    }, [checkForNull]);

    const calculateBasicPrice = useCallback((basicRate, otherCost, numberOfPieces) => {
        return (checkForNull(basicRate) + checkForNull(otherCost)) / checkForNull(numberOfPieces);
    }, [checkForNull]);

    const calculatePercentageChange = useCallback((oldValue, newValue) => {
        const old = checkForNull(oldValue);
        if (old === 0) return 0;
        return ((checkForNull(newValue) - old) / old) * 100;
    }, [checkForNull]);

    const applyPercentage = useCallback((baseValue, percentage) => {
        return checkForNull(baseValue) + (checkForNull(baseValue) * checkForNull(percentage) / 100);
    }, [checkForNull]);

    return {
        checkForNull,
        calculateNetLandedCost,
        calculateBasicPrice,
        calculatePercentageChange,
        applyPercentage
    };
};

// ============================================================================
// Export all hooks
// ============================================================================

export default {
    useSimulationUndo,
    useSimulationGrid,
    useSimulationVBC,
    useSimulationConfig,
    useSimulationCalculations
};
